import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Etape } from './useEtapes';
import { pastilleOk, pulseCTA } from './animations';
import { GRADIENT_VERT, GRADIENT_VERT_HOVER, RAYON, VERT } from './tokens';

interface Props {
  etape: Etape;
  numero: number;
  courante: boolean;
  reduce: boolean;
  d: (ms: number) => number;
  onAction: () => void;
  /**
   * Quand l'action de l'étape mène à l'écran déjà affiché, on remplace le bouton
   * par cette indication : un bouton qui ne produit rien est pire que pas de bouton.
   */
  indication?: string;
}

const EtapeLigne: React.FC<Props> = ({ etape, numero, courante, reduce, d, onAction, indication }) => {
  const pastilleFond = etape.terminee ? VERT : courante ? 'transparent' : '#f0f0f0';
  const pastilleTexte = etape.terminee ? '#fff' : courante ? VERT : '#9e9e9e';

  return (
    <Box
      component="li"
      aria-current={courante ? 'step' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        py: 1,
        listStyle: 'none',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: '50%',
          backgroundColor: pastilleFond,
          border: courante ? `2px solid ${VERT}` : '2px solid transparent',
          color: pastilleTexte,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.85rem',
          fontWeight: 700,
          animation: etape.terminee && !reduce ? `${pastilleOk} 320ms ease-out` : 'none',
        }}
      >
        {etape.terminee ? <CheckIcon sx={{ fontSize: 19 }} /> : numero}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '0.95rem',
            fontWeight: 600,
            color: etape.terminee ? '#8a8a8a' : '#2b2b2b',
            textDecoration: etape.terminee ? 'line-through' : 'none',
            lineHeight: 1.35,
          }}
        >
          {etape.titre}
        </Typography>

        {etape.sousTitre && (
          <Typography
            sx={{
              fontSize: '0.8rem',
              color: '#777',
              lineHeight: 1.4,
              mt: 0.25,
              overflowWrap: 'anywhere',
            }}
          >
            {etape.sousTitre}
          </Typography>
        )}

        {courante && indication && (
          <Typography
            sx={{
              mt: 0.75,
              fontSize: '0.82rem',
              fontWeight: 600,
              color: VERT,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
            {indication}
          </Typography>
        )}

        {courante && !indication && (
          <Button
            variant="contained"
            size="small"
            onClick={onAction}
            sx={{
              mt: 1,
              py: 0.6,
              px: 2,
              borderRadius: RAYON,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              background: GRADIENT_VERT,
              animation: reduce ? 'none' : `${pulseCTA} 1600ms ease-out 2`,
              '&:hover': { background: GRADIENT_VERT_HOVER },
            }}
          >
            {etape.libelleAction}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default EtapeLigne;
